import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  FaAndroid,
  FaBars,
  FaBrain,
  FaChartPie,
  FaCheckCircle,
  FaClock,
  FaCode,
  FaPlayCircle,
  FaTimes,
  FaWhatsapp,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";

import LanguageSwitcher from "../components/LanguageSwitcher";

const LandingBetter = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showHowToVideo, setShowHowToVideo] = useState(false);

  return (
    <>
      {/* WhatsApp Floating Button */}
      <a
        href="https://wa.me/+962790488840" // replace with your number in international format
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-24 right-5 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-green-500 text-white shadow-lg transition-transform hover:scale-110 hover:shadow-xl md:bottom-5"
      >
        <FaWhatsapp size={28} />
      </a>
      <div
        dir={isArabic ? "rtl" : "ltr"}
        className="flex min-h-screen flex-col bg-gray-100 text-sm text-[#616262]"
      >
        {/* Top bar */}
        <div className="border-b border-gray-200 bg-white py-4">
          <div className="mx-auto flex w-full max-w-6xl items-center justify-between gap-3 px-4">
            {/* Logo */}
            <div className="flex items-center gap-4">
              <img
                onClick={() => navigate("/auth/landing")}
                src="/logo-blue.svg"
                alt="Logo"
                className="h-10 w-auto cursor-pointer sm:h-8"
              />
            </div>

            {/* Desktop Buttons */}
            <div className="hidden items-center gap-2 sm:flex">
              <button
                onClick={() => navigate("/auth/institutes-register")}
                className="rounded-full bg-green-600 px-4 py-2 text-sm font-semibold text-white shadow-md transition-transform hover:scale-105 hover:bg-green-800"
              >
                {isArabic ? "تسجيل مؤسسة تعليمية" : "Institutes Register"}
              </button>
              <button
                onClick={() => navigate("/auth/register")}
                className="rounded-full bg-purple-500 px-4 py-2 text-sm font-semibold text-white shadow-md transition-transform hover:scale-105 hover:bg-purple-700"
              >
                {isArabic ? "انشاء حساب" : "Register"}
              </button>
              <button
                onClick={() => navigate("/auth/login")}
                className="rounded-full border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm transition-transform hover:scale-105 hover:border-purple-500 hover:text-purple-600"
              >
                {isArabic ? "تسجيل الدخول" : "Login"}
              </button>
              {/* <LanguageSwitcher defaultTheme /> */}
            </div>
            <div className="flex items-center gap-2 sm:hidden">
              {/* <LanguageSwitcher defaultTheme /> */}
              <button className="p-2 text-purple-500 sm:hidden" onClick={() => setMobileOpen(!mobileOpen)}>
                {mobileOpen ? <FaTimes className="text-2xl" /> : <FaBars className="text-2xl" />}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          <div
            className={`overflow-hidden transition-all duration-300 sm:hidden ${
              mobileOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
            }`}
          >
            <div className="flex flex-col gap-3 p-4">
              <button
                onClick={() => {
                  navigate("/auth/institutes-register");
                  setMobileOpen(false);
                }}
                className="w-full rounded-full bg-green-600 px-4 py-3 text-sm font-semibold text-white shadow-md"
              >
                {isArabic ? "تسجيل مؤسسة تعليمية" : "Institutes Register"}
              </button>

              <button
                onClick={() => {
                  navigate("/auth/register");
                  setMobileOpen(false);
                }}
                className="w-full rounded-full bg-purple-500 px-4 py-3 text-sm font-semibold text-white shadow-md"
              >
                {isArabic ? "انشاء حساب" : "Register"}
              </button>

              <button
                onClick={() => {
                  navigate("/auth/login");
                  setMobileOpen(false);
                }}
                className="w-full rounded-full border border-gray-300 bg-white px-4 py-3 text-sm font-semibold text-gray-700 shadow-sm"
              >
                {isArabic ? "تسجيل الدخول" : "Login"}
              </button>
            </div>
          </div>
        </div>

        {/* Introduction Section */}
        <section className="relative mx-auto max-w-6xl px-6 py-10 pt-16 sm:px-4">
          <div>
            {/* Title + JoCodes Logo */}
            <h2 className="mb-6 flex flex-col items-center justify-center text-center text-3xl font-extrabold leading-snug drop-shadow-xl sm:text-5xl">
              <img src="/joCodesLogo.svg" alt="JoCodes Logo" className="mb-6 h-16 w-auto sm:h-20" />
              <span>{isArabic ? "مبرمجو الأردن - JoCodes" : "Programmers of Jordan – JoCodes"}</span>
            </h2>

            <p className="mx-auto mb-4 text-base leading-relaxed opacity-90">
              {isArabic
                ? "مرحباً بك في HelloGate، البوابة الرسمية لبرنامج  مبرمجو الأردن – JoCodes ، المبادرة الوطنية الطموحة أحد برامج مشروع الشباب والتكنولوجيا والوظائف لدى وزارة الاقتصاد الرقمي والريادة بالتعاون مع مؤسسة ولي العهد. ينفذ البرنامج شركة Hello World Kids ضمن مشروع الشباب والتكنولوجيا والوظائف."
                : "Welcome to HelloGate, the official portal for the Jordan Coders Program - JoCodes, an ambitious national initiative that is part of the Youth, Technology, and Jobs Project of the Ministry of Digital Economy and Entrepreneurship, in collaboration with the Crown Prince Foundation. The program is implemented by Hello World Kids (HWK) as part of the Youth, Technology, and Jobs Project."}
            </p>

            <p className="mx-auto mb-6 text-base leading-relaxed opacity-90">
              {isArabic
                ? "يهدف البرنامج إلى تمكين أكثر من 10,000 شاب وشابة أردني وسوري بالمهارات الرقمية والبرمجية الأكثر طلباً في سوق العمل المحلي والعالمي. "
                : "The program aims to empower more than 10,000 Jordanian and Syrian youth, with the highly in-demand digital and programming skills in both local and global markets"}
            </p>

            {/* Register Button (centered + visible on mobile first screen) */}
            <div className="flex justify-center pt-4">
              <button
                onClick={() => navigate("/auth/register")}
                className="group relative w-full max-w-xs overflow-hidden rounded-full bg-purple-500 px-10 py-4 text-lg font-semibold text-white shadow-2xl transition-all duration-300 hover:scale-105 hover:shadow-2xl sm:w-64"
              >
                {/* Text + Icon */}
                <div className="flex items-center justify-center gap-2">
                  <span>{isArabic ? "سجّل الآن" : "Register Now"}</span>
                </div>
              </button>
            </div>
            {/* <div className="py-0">
              <button
                onClick={() => setShowHowToVideo(true)}
                className="mx-auto mt-6 flex w-fit items-center justify-center gap-2 rounded-lg py-1.5 text-sm font-semibold text-purple-500 underline transition"
              >
                <FaPlayCircle />
                {t("howToRegister2")}
              </button>
            </div> */}
          </div>
        </section>

        <section className="relative mx-auto max-w-6xl px-6 py-10 pt-4 sm:px-4">
          <div>
            <h2 className="mb-10 text-center text-3xl font-bold">
              {isArabic ? "جائزة المؤسسة التعليمية الرقمية" : "Digital Educational Institution Award"}
            </h2>
            {isArabic ? (
              <div className="mx-auto mb-6 space-y-4 text-base leading-relaxed opacity-90">
                <p>
                  تُكرّم جائزة المؤسسة التعليمية الرقمية المدارس والجامعات/الكليات التي تسهم بفاعلية في تمكين
                  المتعلمين من اكتساب المهارات الرقمية المستقبلية، وتحتفي الجائزة بالمؤسسات التعليمية التي
                  تتجاوز مجرد المشاركة، من خلال تشجيع تسجيل الطلبة، ودعم إتمام الدورات، وتعزيز التعاون
                  المؤسسي، وإظهار التزام فعلي طوال دورة تنفيذ البرنامج.
                </p>

                <p>
                  يتم تقييم المؤسسات التعليمية وفق معايير واضحة قائمة على البيانات ومصمّمة بما يتناسب مع
                  السياق التعليمي لكل مؤسسة، بما يضمن العدالة وقابلية المقارنة بين مختلف أنواع المؤسسات
                  وأحجامها. كما يتم تكريم المؤسسات ذات الأداء المتميّز تقديرًا لريادتها في التعليم الرقمي،
                  وقوة تفاعل المتعلمين، والأثر الملموس الذي تحققه.
                </p>

                <p>
                  اطّلع على{" "}
                  <a
                    href="/pdf-2.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline hover:opacity-80"
                  >
                    معايير تقييم المدارس
                  </a>{" "}
                  أو{" "}
                  <a
                    href="/pdf-1.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline hover:opacity-80"
                  >
                    معايير تقييم الجامعات/الكليات
                  </a>{" "}
                  لمعرفة المزيد.
                </p>
              </div>
            ) : (
              <div className="mx-auto mb-6 space-y-4 text-base leading-relaxed opacity-90">
                <p>
                  The Digital Educational Institution Award recognizes schools and universities/colleges that
                  actively empower learners with future-ready digital skills. The award celebrates
                  institutions that go beyond participation by encouraging student enrollment, supporting
                  course completion, fostering strong collaboration, and demonstrating meaningful
                  institutional commitment throughout the program cycle.
                </p>

                <p>
                  Institutions are evaluated using transparent, data-driven criteria tailored to their
                  educational context, ensuring fairness and comparability across different institution types
                  and sizes. High-performing institutions are recognized for their leadership in digital
                  education, strong learner engagement, and measurable impact.
                </p>

                <p>
                  View the{" "}
                  <a
                    href="/pdf-2.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline hover:opacity-80"
                  >
                    Schools Evaluation Criteria
                  </a>{" "}
                  or{" "}
                  <a
                    href="/pdf-1.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline hover:opacity-80"
                  >
                    Universities/Colleges Evaluation Criteria
                  </a>{" "}
                  to learn more.
                </p>
              </div>
            )}

            {/* Institutes Register Button */}
            <div className="flex justify-center pt-4">
              <button
                onClick={() => navigate("/auth/institutes-register")}
                className="group relative w-full max-w-xs overflow-hidden rounded-full bg-green-600 px-10 py-4 text-lg font-semibold text-white shadow-2xl transition-all duration-300 hover:scale-105 hover:shadow-2xl"
              >
                <div className="flex items-center justify-center gap-2">
                  <span>{isArabic ? "تسجيل مؤسسة تعليمية" : "Register Institute"}</span>
                </div>
              </button>
            </div>
          </div>
        </section>

        {/* Learning Tracks FIRST */}
        <section className="mx-auto max-w-6xl px-6 py-10 pt-6 sm:px-4">
          <h2 className="mb-10 text-center text-3xl font-bold">
            {isArabic ? "المسارات التعليمية" : "Learning Tracks"}
          </h2>
          <div className="grid gap-6 sm:grid-cols-2">
            {[
              {
                icon: <FaBrain className="text-3xl text-purple-500" />,
                iconBg: "from-purple-100 to-purple-50",
                title: isArabic ? "أساسيات الذكاء الاصطناعي" : "Fundamentals of Artificial Intelligence",
                description: isArabic
                  ? "يقدّم هذا المسار مدخلًا متكاملًا إلى عالم الذكاء الاصطناعي حيث يغطي المفاهيم الأساسية ومجالاته المختلفة إضافة إلى التعرف على أدوات الذكاء الاصطناعي التوليدي وكيفية توظيفها في مهام عملية، كما يركّز على فهم آلية عمل النماذج الحديثة وتطبيقاتها في مختلف المجالات. يتناول المسار أيضًا الجوانب الأخلاقية المرتبطة بتصميم واستخدام أنظمة الذكاء الاصطناعي مع التركيز على الاستخدام المسؤول والواعي لهذه التقنيات في السياقات الأكاديمية والمهنية."
                  : "This track provides a comprehensive introduction to AI, covering core concepts, generative AI tools, and their practical applications. It also addresses how modern models work and the ethical dimensions of designing and using AI systems responsibly in academic and professional contexts.",
                duration: isArabic
                  ? "15 ساعة (10 ساعات محتوى تعليمي و 5 ساعات تطبيق عملي)"
                  : "15 hours (10 hrs content + 5 hrs practice)",
                durationColor: "text-purple-700 bg-purple-50 border border-purple-200",
              },
              {
                icon: <FaAndroid className="text-3xl text-pink-500" />,
                iconBg: "from-pink-100 to-pink-50",
                title: isArabic
                  ? "أساسيات تطوير تطبيقات أندرويد بلغة Kotlin"
                  : "Android App Development with Kotlin",
                description: isArabic
                  ? "يقدّم هذا المسار أساسيات تطوير تطبيقات أندرويد بدءًا من إعداد بيئة العمل والتعرّف على بنية المشروع وصولًا إلى تصميم واجهات المستخدم وبرمجة التطبيقات باستخدام لغة Kotlin. يركّز المسار على الربط بين التصميم والتطوير لبناء تطبيقات تفاعلية وعملية مع فهم المفاهيم الأساسية اللازمة لتطوير تطبيقات حديثة."
                  : "This track covers Android app development fundamentals, from setting up the environment and understanding the project structure to designing user interfaces and building apps with Kotlin. It focuses on bridging design and development to create interactive, modern applications.",
                duration: isArabic
                  ? "20 ساعة (11 ساعة محتوى تعليمي و 9 ساعات تطبيق عملي)"
                  : "20 hours (11 hrs content + 9 hrs practice)",
                durationColor: "text-pink-700 bg-pink-50 border border-pink-200",
              },
              {
                icon: <FaCode className="text-3xl text-yellow-500" />,
                iconBg: "from-yellow-100 to-yellow-50",
                title: isArabic ? "أساسيات البرمجة" : "Programming Fundamentals",
                description: isArabic
                  ? "يقدّم هذا المسار أساسيات تطوير الويب بدءًا من فهم كيفية عمل الإنترنت وصولًا إلى بناء وتصميم صفحات ويب تفاعلية. يغطي المسار بناء الصفحات باستخدام HTML وتصميم الواجهات باستخدام CSS إضافة إلى برمجة التفاعل والديناميكية باستخدام JavaScript. يركّز المسار على الربط بين هيكلة المحتوى وتصميم الواجهة وإضافة التفاعل، مما يمكّن من إنشاء صفحات ويب متكاملة وجذابة وقابلة للتوسّع باستخدام أحدث الممارسات الأساسية في تطوير الويب."
                  : "This track covers web development fundamentals, from understanding how the internet works to building interactive web pages. It covers HTML for page structure, CSS for styling, and JavaScript for interactivity — teaching you to create complete, attractive, and scalable websites using modern best practices.",
                duration: isArabic
                  ? "27 ساعة (17 ساعة محتوى تعليمي و 10 ساعات تطبيق عملي)"
                  : "27 hours (17 hrs content + 10 hrs practice)",
                durationColor: "text-yellow-700 bg-yellow-50 border border-yellow-200",
              },
              {
                icon: <FaChartPie className="text-3xl text-green-500" />,
                iconBg: "from-green-100 to-green-50",
                title: isArabic ? "أساسيات تحليل البيانات" : "Fundamentals of Data Analysis",
                description: isArabic
                  ? "يقدّم هذا المسار مدخلًا عمليًا إلى عالم البيانات حيث يركّز على فهم أنواع البيانات وطرق تحليلها واستخلاص المعاني منها باستخدام الأساليب الإحصائية الأساسية، كما يغطي استخدام الجداول الإلكترونية لتنظيم البيانات ومعالجتها من خلال الدوال والفرز والتصفية والجداول المحورية. يتناول المسار أيضًا كيفية تمثيل البيانات بصريًا باستخدام الرسوم البيانية، إضافة إلى تطبيق المفاهيم في سياقات واقعية مثل تحليل الأداء والمؤشرات الأساسية في الأعمال مما يمكّن من اتخاذ قرارات مبنية على البيانات بشكل فعّال."
                  : "This track offers a practical introduction to data analysis, focusing on understanding data types, statistical analysis methods, and using spreadsheets for data organization, sorting, filtering, and pivot tables. It also covers data visualization through charts and applying concepts in real-world business performance contexts.",
                duration: isArabic
                  ? "24 ساعة (14 ساعة محتوى تعليمي و 10 ساعات تطبيق عملي)"
                  : "24 hours (14 hrs content + 10 hrs practice)",
                durationColor: "text-green-700 bg-green-50 border border-green-200",
              },
            ].map((track, idx) => (
              <div
                key={idx}
                className="group flex flex-col overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-[0_4px_20px_rgba(0,0,0,0.08)] transition-all duration-300 hover:scale-[1.02] hover:shadow-[0_8px_30px_rgba(0,0,0,0.14)]"
              >
                {/* Colored icon header */}
                <div className={`flex items-center gap-4 bg-gradient-to-br ${track.iconBg} px-6 py-5`}>
                  <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-white shadow-sm">
                    {track.icon}
                  </div>
                  <h3 className="text-base font-bold leading-snug">{track.title}</h3>
                </div>

                {/* Description + duration */}
                <div className="flex flex-1 flex-col p-6">
                  <p className="flex-1 text-sm leading-relaxed opacity-75">
                    {track.description
                      .split(". ")
                      .filter(Boolean)
                      .map((sentence, index, arr) => (
                        <span key={index}>
                          {sentence}
                          {index !== arr.length - 1 && (
                            <>
                              .
                              <br />
                              <br />
                            </>
                          )}
                        </span>
                      ))}
                  </p>{" "}
                  {track.duration && (
                    <div
                      className={`mt-5 flex items-center gap-2 self-start rounded-full px-3 py-1.5 text-xs font-semibold ${track.durationColor}`}
                    >
                      <FaClock className="shrink-0" />
                      <span>{track.duration}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Features (moved after Learning Tracks) */}
        {/* <section className="relative mx-auto max-w-6xl px-6 py-10 sm:px-4">
        <h2 className="mb-6 text-center text-3xl font-bold drop-shadow-xl">
          {isArabic ? "ماذا ستتعلم" : "What You’ll Learn"}
        </h2>
        <ul className="list-disc gap-2 px-6 text-base leading-relaxed">
          {(isArabic
            ? [
                "التعلّم الإلكتروني التفاعلي",
                "جلسات الدعم مع خبراء في البرمجة والمهارات التقنية",
                "ورش عمل وفعاليات ميدانية وإلكترونية",
                "محتوى تعليمي متدرّج ومواكب لسوق العمل",
              ]
            : [
                "Interactive e-learning",
                "Support sessions with programming and technical skills experts",
                "Workshops and both online and offline events",
                "Structured educational content aligned with labor market needs",
              ]
          ).map((item, idx) => (
            <li key={idx}>{item}</li>
          ))}
        </ul>
      </section> */}

        {/* <section className="mx-auto max-w-6xl px-6 py-10 text-center sm:px-4">
          <h2 className="mb-6 text-3xl font-bold drop-shadow-xl">
            {isArabic ? "مدة المسارات" : "Duration of Tracks"}
          </h2>
          <p className="mx-auto max-w-6xl text-base opacity-90">
            {isArabic
              ? "كل مسار تعليمي يستغرق حوالي 7 أسابيع (60–70 ساعة دراسية فعلية)، ويمكنك إنهاؤها بوتيرة أسرع أو دراسة أكثر من مسار في نفس الوقت حسب طاقتك."
              : "Each track takes about 7 weeks (60–70 actual study hours). You can complete the tracks faster or study more than one at the same time depending on your pace."}
          </p>
        </section> */}

        {/* Benefits */}
        <section className="mx-auto max-w-6xl px-6 py-10 sm:px-4">
          <h2 className="mb-8 text-center text-3xl font-bold drop-shadow-xl">
            {isArabic ? "مزايا بوابة HelloGate" : "Benefits of HelloGate"}
          </h2>
          <ul className="grid gap-6 sm:grid-cols-2">
            {(isArabic
              ? [
                  "دعم أكاديمي طوال فترة التعلّم.",
                  "توفير تسجيلات الجلسات المباشرة.",
                  "متابعة أخبار ومعلومات برنامج JoCodes أولاً بأول.",
                  "الاطلاع والمشاركة في فعاليات إلكترونية وميدانية.",
                ]
              : [
                  "Academic support throughout your learning journey.",
                  "Access to recordings of live sessions.",
                  "Stay updated with the latest JoCodes news and announcements.",
                  "Participate in both online and offline events.",
                ]
            ).map((benefit, idx) => (
              <li
                key={idx}
                className="flex items-start gap-2 rounded-xl border border-gray-100 bg-gradient-to-b from-white/80 to-white/60 p-6 text-center shadow-[0_4px_20px_rgba(0,0,0,0.1)] backdrop-blur-md transition-all duration-300 hover:scale-[1.02] hover:shadow-[0_8px_30px_rgba(0,0,0,0.12)]"
              >
                <FaCheckCircle className="mt-0.5 text-lg text-green-400" />
                <span className="text-base">{benefit}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* CTA */}
        <section className="mx-auto max-w-6xl px-6 py-10 text-center sm:px-4">
          <h2 className="mb-4 text-3xl font-bold drop-shadow-xl">
            {isArabic ? "مستقبلك يبدأ بخطوة" : "Your Future Starts Here"}
          </h2>
          <p className="mx-auto mb-8 max-w-6xl text-base opacity-90">
            {isArabic
              ? "سجّل الآن في برنامج مبرمجو الأردن JoCodes عبر بوابة HelloGate وانطلق في رحلتك نحو المهارات الرقمية التي تصنع الفرق!"
              : "Register now for JoCodes through HelloGate and begin your journey towards the digital skills that make a difference!"}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <button
              onClick={() => navigate("/auth/register")}
              className="group relative w-full max-w-xs overflow-hidden rounded-full bg-purple-500 px-10 py-5 text-lg font-semibold text-white shadow-xl transition-all duration-300 hover:scale-105 hover:shadow-2xl sm:w-64"
            >
              {isArabic ? "سجّل الآن" : "Register"}
            </button>
          </div>
        </section>

        {/* Footer Logos */}
        <footer dir="ltr" className="mt-auto border-t border-gray-200 bg-white py-1">
          <div className="flex flex-wrap items-center justify-center gap-6">
            <a href="https://modee.gov.jo" target="_blank" rel="noopener noreferrer">
              <img
                src="/Modee2.png"
                alt="Modee"
                className="h-16 w-[75px] object-contain opacity-90 transition hover:opacity-100 sm:h-20"
              />
            </a>

            <a href="https://cpf.jo" target="_blank" rel="noopener noreferrer">
              <img
                src="/CPF2.png"
                alt="CPF"
                className="h-20 w-20 object-contain opacity-90 transition hover:opacity-100 sm:h-20"
              />
            </a>
            <a href="https://ytj.jo" target="_blank" rel="noopener noreferrer">
              <img
                src="/YTJ2.png"
                alt="YTJ"
                className="h-20 w-20 object-contain opacity-90 transition hover:opacity-100 sm:h-20"
              />
            </a>
          </div>
          <div>
            <p className="text-center text-sm text-gray-600">
              &copy; {new Date().getFullYear()} Designed and developed by HWK
            </p>
          </div>
        </footer>
      </div>
      {showHowToVideo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <div className="relative w-full max-w-5xl rounded-2xl bg-black shadow-xl">
            <button
              onClick={() => setShowHowToVideo(false)}
              className="absolute right-3 top-3 z-10 rounded-full bg-black/60 p-2 text-white hover:bg-black"
            >
              {t("close")}
            </button>

            <div className="relative w-full overflow-hidden rounded-2xl pt-[56.25%]">
              {/* 16:9 */}
              <video
                src="https://d3fe2eyqcfkw6t.cloudfront.net/hellogate/RegistrationTutorial.mp4"
                controls
                controlsList="nodownload"
                className="absolute inset-0 h-full w-full object-contain"
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LandingBetter;
